
import axios, { AxiosResponse, AxiosRequestConfig } from 'axios';
import { Probe, ProbeReading } from '@/types/nfresh';
import { HttpResponse } from '@/types/http';
import { Session } from '@/types/core';
import {Delivery, Subscription} from "@/types/core-push";

export class NfreshClient {

  private nfreshBaseUrl: string = process.env.VUE_APP_NFRESH_API_BASE_URL!;
  
  private currentSession:Session|null = null;

  private baseConfig: any = {
    timeout: 180000,
    validateStatus: () => true,
    headers: {}
  };

  public getCurrentSession():Session|null {

    if(!this.currentSession)
      return null;

    return Object.assign({}, this.currentSession);
  }

  public async getProbe(req: GetProbeReq): Promise<GetProbeReply & HttpResponse> {

    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'get',
      url: `${this.nfreshBaseUrl}/probes/${req.deviceId}`,
      params: {
        _ts: new Date().getTime(),
      },
    });

    const resp = await this.doRequest(cfg);
    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, probe: resp.data as Probe};
  }

  public async getProbes(req: GetProbesReq): Promise<GetProbesReply & HttpResponse> {

    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'get',
      url: `${this.nfreshBaseUrl}/probes`,
      params: {
        skip: req.skip,
        limit: req.limit,
        _ts: new Date().getTime(),
      },
    });

    const resp = await this.doRequest(cfg);
    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, probeList: resp.data as Probe[]};
  }


  public async getProbeReadings(req:GetProbeReadingsReq):Promise<GetProbeReadingsReply & HttpResponse> {

    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'get',
      url: `${this.nfreshBaseUrl}/probes/${req.deviceId}/readings`,
      params: {
        fromTs: req.fromTs,
        toTs: req.toTs,
        limit: req.limit,
        _ts: new Date().getTime(),
      }
    });

    const resp = await this.doRequest(cfg);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, readingList: resp.data as ProbeReading[]};
  }

  public async getProbeLatestReadings(req:GetProbeLatestReadingsReq):Promise<GetProbeReadingsReply & HttpResponse> {

    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'get',
      url: `${this.nfreshBaseUrl}/probes/${req.deviceId}/latest-readings`,
      params: {
        limit: req.limit,
        _ts: new Date().getTime(),
      }
    });

    const resp = await this.doRequest(cfg);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, readingList: resp.data as ProbeReading[]};
  }

  public async getSubscriptions(req:{skip:number, limit:number}):Promise<{subList?:Subscription[]} & HttpResponse> {

    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'get',
      url: `${this.nfreshBaseUrl}/subscriptions`,
      params: {
        skip: req.skip,
        limit: req.limit,
      }
    });

    const resp = await this.doRequest(cfg);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, subList: resp.data as Subscription[]};
  }

  public async getSubscription(req:{subId:number}):Promise<{sub?:Subscription} & HttpResponse> {

    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'get',
      url: `${this.nfreshBaseUrl}/subscriptions/${req.subId}`,
    });

    const resp = await this.doRequest(cfg);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, sub: resp.data as Subscription};
  }

  public async createSub(req:{enterpriseId:string, type:string, endpoint:object,
    deviceType?:string, pushInterval:number, format:string}):Promise<{newSub?:Subscription} & HttpResponse> {

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'post',
      url: `${this.nfreshBaseUrl}/subscriptions`,
      data: req
    });

    const resp = await this.doRequest(cfg);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, newSub: resp.data as Subscription };
  }

  public async deleteSub(req:{subId:number}): Promise<HttpResponse> {

    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'delete',
      url: `${this.nfreshBaseUrl}/subscriptions/${req.subId}`,
    });

    const resp = await this.doRequest(cfg, 204);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg};
  }

  public isLoggedIn(): boolean {
    return this.baseConfig.headers.authorization !== undefined;
  }

  public async getSubDeliveries(req:{subId:number, limit:number}):Promise<{deliveryList?:Delivery[]} & HttpResponse> {

    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'get',
      url: `${this.nfreshBaseUrl}/subscriptions/${req.subId}/deliveries`,
      params: {
        limit: req.limit,
      }
    });

    const resp = await this.doRequest(cfg);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, deliveryList: resp.data as Delivery[]};
  }

  private async doRequest(req: AxiosRequestConfig, expectedHttpCode?: number): Promise<InternalHttpReply> {

    let resp: AxiosResponse;

    try {
      resp = await axios.request(req);
    } catch (err) {
      return {errorMsg: `${err}`};
    }

    // we expect a code 200 by default
    expectedHttpCode = expectedHttpCode !== undefined ? expectedHttpCode : 200;

    if (resp.status !== expectedHttpCode) {
      if (resp.data && resp.data.message) {
        return {httpCode: resp.status, errorMsg: `${resp.data.message} (HTTP ${resp.status})`};
      } else {
        return {httpCode: resp.status, errorMsg: `unexpected HTTP error ${resp.status}`};
      }
    }
    return Promise.resolve({httpCode: resp.status, data: resp.data});
  }

  private restoreSession() {
    const ret = localStorage.getItem('v3-nnnco-session');

    if(!ret)
      return;

    let parsed: Session | undefined;
    try {
      parsed = JSON.parse(ret) as Session;
    } catch (err) { 
      this.clearSession();
      return {};
    }

    // if we don't have a session or if it has expired let's clear the session
    if(!parsed.token || (parsed.expiresAt * 1000) < new Date().getTime())
      return this.clearSession();

    this.currentSession = parsed;

    // we restore the session
    this.baseConfig.headers = {authorization: `Bearer ${parsed.token}`};
  }

  private clearSession() {
    this.baseConfig.headers = {};
    this.currentSession = null;
    localStorage.removeItem('v3-nnnco-session');
  }

}

// get probes
export interface GetProbesReq {
  skip: number;
  limit: number;
}

export interface GetProbesReply {
  probeList: Probe[];
}

// get probe
export interface GetProbeReq {
  deviceId: string;
}

export interface GetProbeReply {
  probe: Probe;
}


interface InternalHttpReply {
  httpCode?: number;
  errorMsg?: string;
  data?: object;
}

// Probe readings
export interface GetProbeReadingsReq {
  deviceId:string;
  fromTs:number;
  toTs:number;
  limit:number;
}

export interface GetProbeReadingsReply {
  readingList:ProbeReading[];
}

// Probe latest readings
export interface GetProbeLatestReadingsReq {
  deviceId:string;
  limit:number;
}

export const curNfreshClient = new NfreshClient();
